import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Container as DraggableContainer } from 'react-smooth-dnd';
import uuid from 'uuid/v4';
import { MenuItem } from '@mui/material';

import { removeSpaces } from '../../../../../utils/string';
import { CoachingActivity } from '../../../../../utils/log';
import useLogger from '../../../../../hooks/useLogger';
import EmptyView from '../../../../components/EmptyView';
import CustomizationContext, { withCustomizationContextReady } from '../../../../context/CustomizationContext';
import ManageClientContext from '../../../../context/ManageClientContext';
import UserContext from '../../../../../context/UserContext';
import useToast from '../../../../hooks/useToast';
import AppCustomHabit from '../../../../Model/AppCustomHabit';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import InputLabel from '../../../../../components/v2/InputLabel';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import HabitItem from './components/HabitItem';

import texts from './texts.json';
import {
  Container,
  Title,
  Description,
  InputContainer,
  StyledHabitInput,
  StyledButton,
  StyledList,
  StyledSelectField,
} from './styles';

const DEFAULT_TAG = 'default';

const HabitCustomization = () => {
  const { customHabitCollection } = useContext(CustomizationContext);
  const [selectedTag, setSelectedTag] = useState(DEFAULT_TAG);
  const [selectedHabitDoc, setSelectedHabitDoc] = useState({});
  const [newHabit, setNewHabit] = useState();
  const [habitList, setHabitList] = useState([]);
  const [habitsDefinition, setHabitsDefinition] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [habitToDelete, setHabitToDelete] = useState(null);
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const isComponentMountedRef = useComponentMounted();

  const {
    userDoc: {
      id: coachId,
    },
  } = useContext(UserContext);

  const {
    clientTagsDocs,
  } = useContext(ManageClientContext);

  const clientTagsList = useMemo(() => ([
    {
      id: DEFAULT_TAG,
      value: DEFAULT_TAG,
      label: texts.defaultTagLabel,
    },
    ...clientTagsDocs.map((doc) => ({
      id: doc.id,
      value: doc.tag,
      label: doc.tag,
    })),
  ]), [clientTagsDocs]);

  useEffect(() => {
    if (isComponentMountedRef) {
      const currentHabitDoc = customHabitCollection.docs.find((doc) => doc.tag === selectedTag);
      if (currentHabitDoc) {
        setSelectedHabitDoc(currentHabitDoc);
        setHabitList(currentHabitDoc.habits);
        setHabitsDefinition(currentHabitDoc.habitsDefinition);
      } else {
        setSelectedHabitDoc({});
        setHabitList([]);
        setHabitsDefinition({});
      }
    }
  }, [
    customHabitCollection,
    isComponentMountedRef,
    selectedTag,
    customHabitCollection.docs.length,
  ]);

  const updateHabitDoc = useCallback(async (updatedHabitList, updatedHabitsDefinition) => {
    setIsLoading(true);
    try {
      if (!selectedHabitDoc.habits) {
        // Create new document for tag.
        const newDoc = await AppCustomHabit.addDoc({
          habits: updatedHabitList,
          habitsDefinition: updatedHabitsDefinition,
          createdAt: new Date(),
          tag: selectedTag,
          coachId,
        });
        setSelectedHabitDoc(newDoc);
      } else if (updatedHabitList.length === 0) {
        // Delete existing document if all habits are deleted.
        await selectedHabitDoc.deleteDoc();
        setSelectedHabitDoc({});
      } else {
        // Update existing document.
        selectedHabitDoc.setHabitsDefinition(updatedHabitsDefinition);
        selectedHabitDoc.setHabits(updatedHabitList);
      }
      setHabitList(updatedHabitList);
      setHabitsDefinition(updatedHabitsDefinition);
      logCoachingActivity(CoachingActivity.UPDATED_HABIT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, [
    coachId,
    selectedHabitDoc,
    logCoachingActivity,
    selectedTag,
  ]);

  const addNewHabit = useCallback(() => {
    if (newHabit) {
      let newHabitList = null;
      let newHabitsDefinition = null;
      let newHabitId = uuid();
      let oldHabitObject = null;
      let isDuplicate = false;

      if (habitsDefinition) {
        Object.keys(habitsDefinition).forEach((habit) => {
          if (removeSpaces(habitsDefinition[habit].text) === removeSpaces(newHabit)) {
            oldHabitObject = habitsDefinition[habit];
            // bring back the deleted habit if there is any by using the old id
            newHabitId = oldHabitObject.id;

            if (habitsDefinition[habit].active) {
              isDuplicate = true;
            }
          }
        });

        if (isDuplicate) {
          showToast(texts.duplicateHabit, { warning: true });
          return;
        }
        newHabitList = [...habitList];
        if (!habitList.includes(newHabitId)) {
          newHabitList.push(newHabitId);
        }
      } else {
        newHabitList = [newHabitId];
      }

      // check new habit in the habit definition. if present change the active status
      if (habitsDefinition && oldHabitObject) {
        newHabitsDefinition = { ...habitsDefinition };
        newHabitsDefinition[oldHabitObject.id].active = true;
      } else {
        newHabitsDefinition = {
          ...habitsDefinition,
          [newHabitId]: {
            text: newHabit,
            id: newHabitId,
            active: true,
          },
        };
      }

      updateHabitDoc(newHabitList, newHabitsDefinition);
      setNewHabit('');
    }
  }, [
    newHabit,
    updateHabitDoc,
    habitList,
    habitsDefinition,
    showToast,
  ]);

  // move position of array element after drag and drop
  const move = (array, from, to) => {
    array.splice(to, 0, array.splice(from, 1)[0]);
    return [...array];
  };

  const onDrop = useCallback(({ removedIndex, addedIndex }) => {
    if (habitList) {
      const newArray = move([...habitList], removedIndex, addedIndex);
      updateHabitDoc(newArray, habitsDefinition);
    }
  }, [updateHabitDoc, habitList, habitsDefinition]);

  const handleConfirmDelete = useCallback(() => {
    if (habitList && habitsDefinition && habitToDelete !== null) {
      const newArray = [...habitList];
      const removedHabit = newArray.splice(habitToDelete, 1)[0];

      const newDefinition = { ...habitsDefinition };
      delete newDefinition[removedHabit];
      updateHabitDoc(newArray, newDefinition);
    }
    setHabitToDelete(null);
  }, [
    updateHabitDoc,
    habitList,
    habitsDefinition,
    habitToDelete,
  ]);

  const handleEdit = useCallback((editedIndex, editedText) => {
    if (habitList && habitsDefinition) {
      const editedHabitId = habitList[editedIndex];
      const newDefinition = { ...habitsDefinition };
      newDefinition[editedHabitId].text = editedText;
      updateHabitDoc(habitList, newDefinition);
    }
  }, [
    updateHabitDoc,
    habitList,
    habitsDefinition,
  ]);

  const handleHide = useCallback((hiddenIndex) => {
    if (habitList && habitsDefinition) {
      const hiddenHabit = habitList[hiddenIndex];
      const newDefinition = { ...habitsDefinition };
      newDefinition[hiddenHabit].active = !newDefinition[hiddenHabit].active;
      updateHabitDoc(habitList, newDefinition);
    }
  }, [
    updateHabitDoc,
    habitList,
    habitsDefinition,
  ]);

  return (
    <Container>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>
      <InputLabel>{texts.clientTag}</InputLabel>
      <StyledSelectField
        value={selectedTag}
        onChange={(e) => setSelectedTag(e.target.value)}
      >
        {clientTagsList.map((tag) => (
          <MenuItem key={tag.id} value={tag.value}>
            {tag.label}
          </MenuItem>
        ))}
      </StyledSelectField>
      <InputContainer>
        <StyledHabitInput
          variant="outlined"
          label={texts.newHabit}
          value={newHabit}
          onChange={(evt) => setNewHabit(evt.target.value)}
        />
        <StyledButton
          variant="contained"
          onClick={addNewHabit}
        >
          {texts.addHabit}
        </StyledButton>
      </InputContainer>
      <Description>{texts.habits}</Description>
      <StyledList>
        <DraggableContainer dragHandleSelector=".drag-handle" lockAxis="y" onDrop={onDrop}>
          {habitList.length > 0 ? (
            habitList.map((habitId, index) => {
              const habit = habitsDefinition?.[habitId];
              return (
                <HabitItem
                  index={index}
                  handleDelete={() => setHabitToDelete(index)}
                  habitText={habit.text}
                  key={habitId}
                  handleEdit={handleEdit}
                  handleHide={handleHide}
                  isHidden={!habit.active}
                />
              );
            })
          ) : <EmptyView content={selectedTag === DEFAULT_TAG ? texts.noDefaultHabits : texts.noCustomHabits} />}

        </DraggableContainer>
      </StyledList>
      <LoadingOverlay isLoading={isLoading} />
      {!!habitToDelete && (
      <ConfirmDialog
        isOpen={!!habitToDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setHabitToDelete(null)}
        dialogTexts={{
          title: texts.deleteHabitContent,
          content: `${habitsDefinition[habitList[habitToDelete]]?.text || ''}`,
        }}
      />
      )}
    </Container>
  );
};

export default compose(
  withCustomizationContextReady,
  observer,
)(HabitCustomization);
